import { SITE, PRICING } from './site';

/**
 * Typed configuration for page-specific structured data.
 * Each path maps to optional Service, FAQ, AggregateRating, and Breadcrumb data.
 * Used by `schemaFor(path)` to generate JSON-LD blocks without hardcoding.
 */

interface ServiceConfig {
  name: string;
  description: string;
  pricePerUnit: string; // band-1 headline rate, e.g. "0.12" — larger homes pay less per sqft
  unitText: string; // e.g., "per square foot"
}

interface FaqItem {
  question: string;
  answer: string;
}

interface PageSchemaConfig {
  service?: ServiceConfig;
  faqs?: FaqItem[];
  includeRating?: boolean; // If true, render AggregateRating for this page
}

/**
 * Map paths to their schema requirements.
 * These are additive: Service pages ALSO get AggregateRating if includeRating=true.
 */
export const PAGE_SCHEMAS: Record<string, PageSchemaConfig> = {
  // Service pages — each gets Service schema + AggregateRating
  '/weekly': {
    service: {
      name: 'Weekly Cleaning Service',
      description:
        'Professional weekly home cleaning with the same crew every visit. Consistent technicians, transparent per-sq-ft pricing, W-2 employees.',
      pricePerUnit: PRICING.weeklyRate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'Do I get the same crew each visit?',
        answer: 'Yes. We assign the same cleaning technicians to your home every week, so they know your preferences and your space inside and out.',
      },
      {
        question: 'What if I need to skip a week?',
        answer: 'Just let us know. You can pause, reschedule, or cancel anytime with no penalty.',
      },
      {
        question: 'What\'s included in a weekly clean?',
        answer: 'Full home cleaning: dusting, vacuuming, bathrooms, kitchen, floors, and all surfaces. Customizable based on your home and preferences.',
      },
    ],
  },

  '/biweekly': {
    service: {
      name: 'Biweekly Cleaning Service',
      description:
        'Professional biweekly home cleaning. Same crew, same standards, every visit. Ideal for homes that need less frequent maintenance.',
      pricePerUnit: PRICING.biweeklyRate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'Why biweekly vs weekly?',
        answer: 'Biweekly is ideal for smaller homes or those with fewer occupants. Same crew and quality at a lower frequency.',
      },
      {
        question: 'How is biweekly priced?',
        answer: 'Biweekly is priced at a slightly higher per-square-foot rate than weekly, reflecting the deeper cleaning needed between visits.',
      },
      {
        question: 'Can I upgrade to weekly?',
        answer: 'Absolutely. You can upgrade to weekly, monthly, or switch to a one-time service anytime.',
      },
    ],
  },

  '/monthly': {
    service: {
      name: 'Monthly Cleaning Service',
      description:
        'Professional monthly home cleaning with per-square-foot pricing. Keep your home fresh on a monthly schedule.',
      pricePerUnit: PRICING.monthly4Rate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'Is monthly cleaning enough?',
        answer: 'It depends on your home, lifestyle, and preferences. Monthly works best for smaller spaces or homes with light traffic.',
      },
      {
        question: 'What\'s the price for monthly?',
        answer: 'Monthly service is our highest per-square-foot rate, reflecting the deeper cleaning needed in a single visit to maintain your home.',
      },
      {
        question: 'What\'s included?',
        answer: 'Full home cleaning with the same attention to detail as our weekly and biweekly services.',
      },
    ],
  },

  '/maintenance': {
    service: {
      name: 'Maintenance Cleaning Service',
      description:
        'Professional one-time maintenance cleaning. Ideal for between-visit touch-ups or first-time deep clean clients.',
      pricePerUnit: PRICING.maintenanceRate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'What\'s the difference between maintenance and deep clean?',
        answer: 'Maintenance is a lighter, single-visit clean for homes in good condition. Deep clean is a comprehensive top-to-bottom service.',
      },
      {
        question: 'When should I book this?',
        answer: 'Maintenance is perfect for touch-ups between recurring visits, before guests arrive, or to add to a recurring service as needed.',
      },
    ],
  },

  '/deep': {
    service: {
      name: 'Deep Clean Service',
      description:
        'Comprehensive top-to-bottom deep cleaning. Required for first-time recurring clients, or available as a standalone service.',
      pricePerUnit: PRICING.fullDeepRate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'What\'s included in a deep clean?',
        answer: 'A comprehensive top-to-bottom clean: baseboards, ceiling fans, inside cabinets, behind appliances, windows, and every surface.',
      },
      {
        question: 'Is a deep clean required before recurring service?',
        answer: 'For most recurring clients, yes. A deep clean establishes the baseline and prepares your home for ongoing maintenance cleanings.',
      },
    ],
  },

  '/move-in-out': {
    service: {
      name: 'Move-In / Move-Out Cleaning',
      description:
        'Unfurnished home cleaned top-to-bottom. Perfect for move-in inspections, turnover cleanings, or vacant property prep.',
      pricePerUnit: PRICING.moveOutRate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'What does unfurnished mean?',
        answer: 'Unfurnished means the home is vacant. We clean all surfaces, fixtures, and spaces accessible to a cleaning team.',
      },
      {
        question: 'Are walls included?',
        answer: 'Walls are excluded from the move-in/move-out service. We focus on all other surfaces, floors, fixtures, and appliances.',
      },
      {
        question: 'How long does it take?',
        answer: 'Duration depends on the home\'s size and condition. We provide estimates based on square footage.',
      },
    ],
  },

  '/residential': {
    service: {
      name: 'Residential Cleaning Services',
      description:
        'Professional residential cleaning across recurring, deep, and move-in/move-out services. Transparent pricing, W-2 employees, consistent crew.',
      pricePerUnit: PRICING.weeklyRate.toString(),
      unitText: 'per square foot',
    },
    includeRating: true,
    faqs: [
      {
        question: 'How much does house cleaning cost in St. Petersburg, FL?',
        answer: `A 1,500 sq ft home is $${(1500 * PRICING.biweeklyRate).toFixed(0)} biweekly, $${(1500 * PRICING.monthly4Rate).toFixed(0)} every four weeks, or $${(1500 * PRICING.fullDeepRate).toFixed(0)} for a full deep clean. We price by square footage at $${PRICING.biweeklyRate.toFixed(2)} per sq ft for biweekly service, with a $${PRICING.minimum} minimum on any job. The calculator on this page gives you the exact number for your home before you contact us.`,
      },
      {
        question: 'What services do you offer?',
        answer: 'Weekly, biweekly and every-four-weeks recurring cleaning; one-time cleans; full deep cleans; and move-in/move-out cleans for empty homes. Residential only.',
      },
      {
        question: 'How is pricing calculated?',
        answer: 'By square footage, furnishing state and service type. The rate drops as your home gets larger and as your visits get more frequent, because a home we see every week takes less time to keep right. There are no hidden fees, trip charges or supply charges.',
      },
      {
        question: 'Do I need to be home for the cleaning?',
        answer: 'No. Most clients give us a key, a door code or a lockbox. We send timestamped photos when the visit is done, so you can see the work whether you were there or not.',
      },
      {
        question: 'Do I get the same cleaners every visit?',
        answer: 'Yes. Your home is assigned to a specific crew, not to whoever is free that morning. They learn your home — which floors mark, which door sticks, where things belong.',
      },
      {
        question: 'Are your cleaners employees or contractors?',
        answer: 'Every cleaning technician is a W-2 employee — payroll taxes, workers compensation, fair wages and a growth path. We do not use gig apps or 1099 subcontractors. That means anyone in your home is covered by our insurance and bonding.',
      },
      {
        question: 'Are you insured and bonded?',
        answer: 'Yes. We carry liability insurance and bonding, and every technician is background checked before their first visit. If something is damaged, it is our problem to fix, not yours.',
      },
      {
        question: 'What cleaning products do you use?',
        answer: 'Non-toxic, eco-friendly products that are safe around kids and pets. If you have a surface that needs something specific, or a product you would rather we use, tell us and we will use yours.',
      },
      {
        question: 'Do I need a contract?',
        answer: 'No. There is no contract, no sign-up fee and no cancellation penalty. Recurring clients can pause, reschedule or stop at any time.',
      },
      {
        question: 'What is the difference between a deep clean and a regular clean?',
        answer: 'A regular clean maintains a home that is already in good shape. A deep clean resets one that is not — it adds vents, interior windows and tracks, behind and beneath movable furniture, grout, trim and baseboards, and degreased cabinet fronts. Most first-time clients on a recurring plan add a deep clean to their first visit.',
      },
      {
        question: 'Do you clean commercial spaces or short-term rentals?',
        answer: 'No. We are residential only — no offices, no Airbnbs, no post-construction surge work. Cleaning the same kind of home every week is what lets us actually know what a crew can do.',
      },
      {
        question: 'What areas do you serve?',
        answer: 'St. Petersburg and the surrounding Greater Tampa Bay area, including Old Northeast, Snell Isle, Historic Kenwood, Crescent Heights, Shore Acres and Bayshore.',
      },
      {
        question: 'How far in advance should I book?',
        answer: 'Usually a few days for a recurring start, and about a week for a deep clean or a move-out, which take longer and need a bigger block of time. Email us if you need something sooner and we will tell you honestly whether we can do it.',
      },
      {
        question: 'What if I am not happy with the clean?',
        answer: 'Tell us within 24 hours and we come back and re-clean the area at no charge. No argument and no questions.',
      },
    ],
  },

  // Home page
  '/': {
    includeRating: true,
  },

  // Pages with FAQs (area pages likely have FAQs)
  '/service-areas': {
    includeRating: true,
  },

  // About page
  '/about': {
    includeRating: true,
  },

  // Reviews page
  '/reviews': {
    includeRating: true,
  },

  // Pricing page
  '/pricing': {
    includeRating: true,
    faqs: [
      {
        question: 'How much does a house cleaning cost?',
        answer: `It depends on the size of the home and how often we come. At 1,500 sq ft: $${(1500 * PRICING.weeklyRate).toFixed(0)} weekly (subject to the $${PRICING.minimum} minimum), $${(1500 * PRICING.biweeklyRate).toFixed(0)} biweekly, $${(1500 * PRICING.monthly4Rate).toFixed(0)} every four weeks, $${(1500 * PRICING.maintenanceRate).toFixed(0)} for a one-time clean and $${(1500 * PRICING.fullDeepRate).toFixed(0)} for a full deep clean.`,
      },
      {
        question: 'Why is your pricing published when other companies quote by phone?',
        answer: 'Because square footage is the one thing about your home we can both measure the same way, so there is no reason to make you call to find out the number. The price on this page is the price we book.',
      },
      {
        question: 'Does the price change after you see the house?',
        answer: 'No. The quote is binding on the square footage you give us. If the home turns out to be materially larger than stated, we tell you before the visit and re-quote — never after the work is done.',
      },
      {
        question: 'Is there a minimum charge?',
        answer: `Yes — $${PRICING.minimum} on any job, and $${PRICING.deepMinimum} on a deep clean. Even a small home needs a real visit, and the minimum is what makes that visit worth a crew's time.`,
      },
      {
        question: 'Do larger homes pay less per square foot?',
        answer: 'Yes. The rate is banded, so square footage past the first 1,500 is charged at a lower rate, and past 3,000 lower again. Cleaning a big house is not proportionally more work.',
      },
      {
        question: 'Do you charge extra for supplies, travel or weekends?',
        answer: 'No. Supplies, equipment and travel inside our service area are included in the rate. There is no fuel surcharge and no weekend premium.',
      },
      {
        question: 'How do I pay?',
        answer: 'Card on file, charged after each visit. No deposit and no prepayment.',
      },
    ],
  },
};

/**
 * Get the schema configuration for a given path.
 * Returns the config object if found, or an empty object if the path has no special schema.
 */
export function getPageSchema(path: string): PageSchemaConfig {
  return PAGE_SCHEMAS[path] ?? {};
}


/**
 * The FAQs for a page — the SAME list that goes into its JSON-LD.
 *
 * Pages used to hardcode their own <details> markup while page-schemas.ts
 * separately emitted FAQPage structured data, so the two could (and did) drift.
 * Google suppresses an FAQ rich result when the markup and the visible text
 * disagree, so a drifted page loses the SEO benefit it was marked up for.
 * Render from here and they cannot diverge.
 */
export function faqsFor(path: string): FaqItem[] {
  return PAGE_SCHEMAS[path]?.faqs ?? [];
}

export type { FaqItem };
